// Computes the breadcrumb segments shown in the top toolbar (AppHeader).
//
// Most routes start with the root "Moria" crumb; Settings instead starts with
// the route the user came from so the breadcrumb is also the contextual return
// path. The avatar beside the breadcrumb remains the stable /projects link.
//
// Native project naming
// lives in `routeLabels.ts:projectDisplayName` so standard route crumbs and
// the contextual Settings return crumb resolve to the same string.
import { computed, type ComputedRef } from 'vue'
import { useRoute, type RouteLocationNormalizedLoaded, type RouteLocationRaw } from 'vue-router'
import { getRouteLabel, projectDisplayName } from '@/lib/routeLabels'
import { useAssayEnvelopeExtractionsStore } from '@/stores/assayEnvelopeExtractions'
import { useAssayEnvelopeWorkspaceStore } from '@/stores/assayEnvelopeWorkspace'
import { useNavigationStore } from '@/stores/navigation'
import { useNativeWorkbooksStore } from '@/stores/nativeWorkbooks'
import { useReviewWorkbookPaneStore } from '@/stores/reviewWorkbookPane'
import { useSourceFilesStore } from '@/stores/sourceFiles'
import { useProjectsStore } from '@/stores/projects'
import type { TreeKind } from '@/types/extractions'
import { extractionQueueQuery, fileWorkspaceQuery } from '@/router/extractionRoutes'

// `kind` controls how AppHeader renders the segment:
//   - 'crumb'   → default route crumb ("Moria", "Haranga", "Extractions")
//   - 'path'    → resource path/name appended to a workspace crumb.
//                 Rendered monospaced, lighter weight, with a `›` lead-in
//                 separator so it doesn't read as further routable hierarchy.
//   - 'context' → tight chip used for the current resource identity.
export interface BreadcrumbSegment {
  label: string
  to?: RouteLocationRaw
  kind?: 'crumb' | 'path' | 'context'
}

// Labels for project sub-section tabs. Overview is intentionally omitted —
// the project label is the final crumb on the overview tab.
const PROJECT_SECTION_LABELS: Record<string, string> = {
  'project-files': 'Source Files',
  'project-extractions': 'Quality Control',
  'project-review-workbook': 'Quality Control',
}

function sourceFileBreadcrumb(
  route: RouteLocationNormalizedLoaded,
  sourceFiles: ReturnType<typeof useSourceFilesStore>,
  root: BreadcrumbSegment,
): BreadcrumbSegment[] {
  const { id, label } = projectIdentity(route)
  const fileId = typeof route.params.fileId === 'string' ? route.params.fileId : ''
  const file =
    sourceFiles.activeProjectId === id && sourceFiles.selectedFile?.id === fileId
      ? sourceFiles.selectedFile
      : null
  const listTarget = {
    name: 'project-files',
    params: { id: String(id) },
    query: fileWorkspaceQuery(route.query),
  }
  return [
    root,
    { label, to: { name: 'project-overview', params: { id: String(id) } } },
    { label: 'Source Files', to: listTarget },
    { label: file?.name || fileId || 'File', kind: 'path' },
  ]
}

const ADMIN_SECTION_LABELS: Record<string, string> = {
  'admin-users': 'Users',
  'admin-security': 'Security',
  'admin-integrations': 'Integrations',
}

const CUSTOMER_ROUTE_NAMES = new Set([
  'customer-workbooks',
  'customer-settings',
  'customer-files',
  'customer-file',
])

function projectIdentity(route: RouteLocationNormalizedLoaded) {
  const id = Number(route.params.id)
  return { id, label: Number.isFinite(id) ? projectDisplayName(id) : 'Project' }
}

function customerBreadcrumb(
  route: RouteLocationNormalizedLoaded,
  sourceFiles: ReturnType<typeof useSourceFilesStore>,
  nativeWorkbooks: ReturnType<typeof useNativeWorkbooksStore>,
): BreadcrumbSegment[] {
  const { id, label } = projectIdentity(route)
  const target = {
    name: useProjectsStore().featuresFor(id).supports_workbook
      ? 'customer-workbooks'
      : 'customer-files',
    params: { id: String(id) },
  }
  const root: BreadcrumbSegment = { label: 'Moria', to: target }
  const project: BreadcrumbSegment = { label, to: target }
  if (route.name === 'customer-settings') return [root, project, { label: 'Settings' }]
  if (route.name === 'customer-files') return [root, project, { label: 'Source Files' }]
  if (route.name === 'customer-file') {
    const fileId = typeof route.params.fileId === 'string' ? route.params.fileId : ''
    const file =
      sourceFiles.activeProjectId === id && sourceFiles.selectedFile?.id === fileId
        ? sourceFiles.selectedFile
        : null
    return [
      root,
      project,
      {
        label: 'Source Files',
        to: {
          name: 'customer-files',
          params: { id: String(id) },
          query: fileWorkspaceQuery(route.query),
        },
      },
      { label: file?.name || fileId || 'File', kind: 'path' },
    ]
  }
  const workbookLabel = workbookDetailLabel(route, nativeWorkbooks)
  if (!workbookLabel) return [root, project, { label: 'Workbooks' }]
  return [root, project, { label: 'Workbooks', to: target }, { label: workbookLabel, kind: 'path' }]
}

function workbookDetailLabel(
  route: RouteLocationNormalizedLoaded,
  nativeWorkbooks: ReturnType<typeof useNativeWorkbooksStore>,
): string | null {
  if (typeof route.query.workbook_id === 'string') {
    const projectId = Number(route.params.id)
    if (!Number.isFinite(projectId)) return 'Workbook'
    return nativeWorkbooks.detailFor(projectId, route.query.workbook_id)?.display_name || 'Workbook'
  }
  if (typeof route.query.path === 'string') {
    return route.query.path.split('/').pop() || 'Workbook'
  }
  return null
}

function projectSectionBreadcrumb(
  route: RouteLocationNormalizedLoaded,
  name: string,
  root: BreadcrumbSegment,
  nativeWorkbooks: ReturnType<typeof useNativeWorkbooksStore>,
): BreadcrumbSegment[] {
  const { id, label } = projectIdentity(route)
  const crumbs: BreadcrumbSegment[] = [
    root,
    { label, to: { name: 'project-overview', params: { id: String(id) } } },
    { label: PROJECT_SECTION_LABELS[name] },
  ]
  const workbookLabel =
    name === 'project-review-workbook' ? workbookDetailLabel(route, nativeWorkbooks) : null
  if (workbookLabel) {
    crumbs[crumbs.length - 1] = {
      label: 'Quality Control',
      to: {
        name: 'project-review-workbook',
        params: { id: String(id) },
        query: { ...route.query, path: undefined, workbook_id: undefined },
      },
    }
    crumbs.push({ label: workbookLabel, kind: 'path' })
  }
  return crumbs
}

function extractionBreadcrumb(
  route: RouteLocationNormalizedLoaded,
  root: BreadcrumbSegment,
): BreadcrumbSegment[] {
  const { id, label } = projectIdentity(route)
  const certSlug = typeof route.params.cert === 'string' ? route.params.cert : ''
  const channel = route.query.data_type === 'channels'
  const queueQuery = {
    ...extractionQueueQuery(route.query),
    ...(channel ? { type: 'channel' } : {}),
  }
  return [
    root,
    { label, to: { name: 'project-overview', params: { id: String(id) } } },
    {
      label: 'Quality Control',
      to: {
        name: 'project-extractions',
        params: { id: String(id) },
        ...(Object.keys(queueQuery).length ? { query: queueQuery } : {}),
      },
    },
    {
      label: 'Extraction',
      to: {
        name: 'project-extractions',
        params: { id: String(id) },
        ...(Object.keys(queueQuery).length ? { query: queueQuery } : {}),
      },
      kind: 'context',
    },
    { label: certSlug || 'File', kind: 'path' },
  ]
}

function isEnvelopeReviewRoute(name: string): boolean {
  return name === 'project-extraction-assay' || name === 'project-extraction-channel'
}

function isProjectOverviewRoute(name: string): boolean {
  return name === 'project-detail' || name === 'project-overview'
}

function envelopeReviewTreeKind(route: RouteLocationNormalizedLoaded): TreeKind {
  return route.name === 'project-extraction-channel' ? 'channel' : 'assay'
}

function envelopeReviewBreadcrumb(
  route: RouteLocationNormalizedLoaded,
  envelopeExtractions: ReturnType<typeof useAssayEnvelopeExtractionsStore>,
  envelopeWorkspace: ReturnType<typeof useAssayEnvelopeWorkspaceStore>,
  root: BreadcrumbSegment,
): BreadcrumbSegment[] {
  const { id, label } = projectIdentity(route)
  const fileId = typeof route.params.fileId === 'string' ? route.params.fileId : ''
  const treeKind = envelopeReviewTreeKind(route)
  const channel = treeKind === 'channel'
  const file = envelopeWorkspace.fileById(id, fileId, treeKind).value
  const scopeId = file?.scope_id ?? ''
  const extraction = scopeId
    ? envelopeExtractions.extractionById(id, scopeId, treeKind).value
    : null
  const queueQuery = {
    ...extractionQueueQuery(route.query),
    ...(channel ? { type: 'channel' } : {}),
  }
  const queueTarget = {
    name: 'project-extractions',
    params: { id: String(id) },
    ...(Object.keys(queueQuery).length ? { query: queueQuery } : {}),
  }
  return [
    root,
    { label, to: { name: 'project-overview', params: { id: String(id) } } },
    { label: 'Quality Control', to: queueTarget },
    {
      label: extraction?.name?.trim() || 'Extraction',
      to: queueTarget,
      kind: 'context',
    },
    { label: file?.basename || fileId || 'File', kind: 'path' },
  ]
}

export function useBreadcrumb(): ComputedRef<BreadcrumbSegment[]> {
  const route = useRoute()
  const envelopeExtractions = useAssayEnvelopeExtractionsStore()
  const envelopeWorkspace = useAssayEnvelopeWorkspaceStore()
  const sourceFiles = useSourceFilesStore()
  const nativeWorkbooks = useNativeWorkbooksStore()
  const navigation = useNavigationStore()
  const workbookPane = useReviewWorkbookPaneStore()

  function appendWorkbookSource(crumbs: BreadcrumbSegment[]): BreadcrumbSegment[] {
    if (route.name !== 'project-review-workbook' && route.name !== 'customer-workbooks')
      return crumbs
    const source = workbookPane.source
    if (
      source?.projectId === Number(route.params.id) &&
      source.workbookId === route.query.workbook_id
    ) {
      crumbs.push({ label: source.filename, kind: 'path' })
    }
    return crumbs
  }

  return computed<BreadcrumbSegment[]>(() => {
    const name = typeof route.name === 'string' ? route.name : ''
    const root: BreadcrumbSegment = { label: 'Moria', to: { name: 'projects' } }

    // /projects → root is the final crumb (drop its link).
    if (name === 'projects' || name === '') {
      return [{ label: 'Moria' }]
    }

    if (CUSTOMER_ROUTE_NAMES.has(name)) {
      const crumbs = customerBreadcrumb(route, sourceFiles, nativeWorkbooks)
      return appendWorkbookSource(crumbs)
    }

    // Project routes.
    if (isProjectOverviewRoute(name)) {
      return [root, { label: projectIdentity(route).label }]
    }
    if (name in PROJECT_SECTION_LABELS) {
      const crumbs = projectSectionBreadcrumb(route, name, root, nativeWorkbooks)
      return appendWorkbookSource(crumbs)
    }
    if (name === 'project-file') {
      return sourceFileBreadcrumb(route, sourceFiles, root)
    }

    // Extraction workspace (cert detail). Breadcrumb absorbs run identity plus
    // the historical cert slug until the V2 resolver replaces the route with
    // the native file identity.
    if (name === 'project-extraction-cert') {
      return extractionBreadcrumb(route, root)
    }
    if (isEnvelopeReviewRoute(name)) {
      return envelopeReviewBreadcrumb(route, envelopeExtractions, envelopeWorkspace, root)
    }

    // Settings.
    if (name === 'settings') {
      const previous = navigation.previousRoute
      const returnCrumb: BreadcrumbSegment = previous
        ? { label: getRouteLabel(previous) || 'Previous page', to: previous.fullPath }
        : { label: 'Projects', to: { name: 'projects' } }
      return [returnCrumb, { label: 'App settings' }]
    }

    // Admin.
    if (name in ADMIN_SECTION_LABELS) {
      return [root, { label: 'Admin' }, { label: ADMIN_SECTION_LABELS[name] }]
    }

    return [{ label: 'Moria' }]
  })
}
